import React from "react";
import { CircleCheckBig } from "lucide-react";
import { quoteCalculatorData } from "../../data/mockData";
import { PhotoUploader } from "./PhotoUploader";

/* ── Success State ── */

const SummaryRow = ({ label, value }) => (
  <div className="flex justify-between gap-4 border-b border-slate-100 py-2 text-sm last:border-0">
    <span className="text-slate-500">{label}</span>
    <span className="text-right font-semibold text-slate-900">{value}</span>
  </div>
);

export const ukDate = (iso) => new Date(`${iso}T12:00:00`).toLocaleDateString("en-GB", { weekday: "short", day: "numeric", month: "short", year: "numeric" }).replace(",", "");

const QuoteFormSuccess = ({ quote, onReset }) => (
  <div id="quote-form" className="self-start scroll-mt-28 rounded-xl bg-white shadow-2xl border-2 border-emerald-100" data-testid="quote-confirmation">
    <div className="p-8 space-y-6">
      <div className="text-center space-y-3">
        <div className="w-16 h-16 bg-green-100 rounded-full flex items-center justify-center mx-auto">
          <CircleCheckBig className="w-8 h-8 text-green-600" />
        </div>
        <h4 className="text-2xl font-bold text-slate-900">Quote request received</h4>
        <p className="text-slate-600">
          Thank you, {quote.name.split(" ")[0]}. A confirmation has been sent to <strong>{quote.email}</strong>. We'll contact you to confirm your price and date.
        </p>
      </div>
      <div className="rounded-lg bg-slate-50 px-5 py-3" data-testid="quote-confirmation-summary">
        <SummaryRow label="Reference" value={quote.id.slice(0, 8).toUpperCase()} />
        <SummaryRow label="Service" value={quote.selectedService} />
        <SummaryRow label="Estimated price" value={`£${quote.estimatedPrice}`} />
        <SummaryRow label="Frequency" value={quote.frequency} />
        <SummaryRow label="Postcode" value={quote.postcode || "-"} />
        <SummaryRow label="Preferred dates" value={quote.preferredDates?.map(ukDate).join(" or ") || "-"} />
        <SummaryRow label="Photos" value={quote.photoIds?.length || 0} />
      </div>
      <p className="text-xs text-slate-500 text-center">This is an estimate. Your final price is confirmed after we review your details.</p>
      <button type="button" onClick={onReset} className="mx-auto block text-sm font-semibold text-sky-700 hover:underline" data-testid="quote-new-request-btn">Make another request</button>
    </div>
  </div>
);

/* ── Input Field ── */

const FormInput = ({ label, required, ...inputProps }) => (
  <div className="space-y-2">
    <label className="text-sm font-semibold text-slate-700">
      {label} {required && "*"}
    </label>
    <input
      className="flex w-full rounded-md border border-slate-300 bg-transparent px-3 py-1 text-base shadow-sm transition-colors placeholder:text-slate-400 focus:outline-none focus:ring-2 focus:ring-sky-600 h-12"
      required={required}
      {...inputProps}
    />
  </div>
);

/* ── Property Type Select ── */

const PropertyTypeSelect = ({ value, onChange }) => (
  <div className="space-y-2">
    <label className="text-sm font-semibold text-slate-700">Property Type *</label>
    <select
      name="propertyType"
      required
      value={value}
      onChange={onChange}
      className="w-full h-12 px-3 rounded-md border border-slate-300 focus:outline-none focus:ring-2 focus:ring-sky-600 bg-white text-slate-900"
    >
      {quoteCalculatorData.propertyTypes.map((pt) => (
        <option key={pt.value} value={pt.value}>
          {pt.label}
        </option>
      ))}
    </select>
  </div>
);

/* ── Message Textarea ── */

const MessageField = ({ value, onChange }) => (
  <div className="space-y-2">
    <label className="text-sm font-semibold text-slate-700">Additional Details</label>
    <textarea
      className="flex min-h-[60px] w-full rounded-md border border-slate-300 bg-transparent px-3 py-2 text-base shadow-sm placeholder:text-slate-400 focus:outline-none focus:ring-2 focus:ring-sky-600 resize-none"
      name="message"
      placeholder="Tell us about your property size, specific cleaning needs, preferred date..."
      rows="5"
      value={value}
      onChange={onChange}
    />
  </div>
);

/* ── Main Form ── */

const QuoteForm = ({ formData, onFormChange, onSubmit, submitted, submitting, photos, setPhotos, onReset }) => {
  if (submitted) return <QuoteFormSuccess quote={submitted} onReset={onReset} />;
  const minDate = new Date(Date.now() + 86400000).toISOString().slice(0, 10);

  return (
    <div id="quote-form" className="rounded-xl bg-white shadow-2xl border-2 border-sky-100">
      <div className="p-8">
        <form onSubmit={onSubmit} className="relative space-y-6">
          <div className="grid md:grid-cols-2 gap-6">
            <FormInput
              label="Full Name"
              required
              placeholder="John Smith"
              value={formData.name}
              name="name"
              onChange={onFormChange}
            />
            <FormInput
              label="Email Address"
              required
              placeholder="john@example.com"
              type="email"
              value={formData.email}
              name="email"
              onChange={onFormChange}
            />
          </div>

          <div className="grid md:grid-cols-2 gap-6">
            <FormInput
              label="Phone Number"
              required
              placeholder="0749 286 5011"
              type="tel"
              value={formData.phone}
              name="phone"
              onChange={onFormChange}
            />
            <PropertyTypeSelect
              value={formData.propertyType}
              onChange={onFormChange}
            />
          </div>

          <div className="grid md:grid-cols-3 gap-6">
            <FormInput label="Postcode" required placeholder="CF10 1AA" value={formData.postcode} name="postcode" onChange={onFormChange} maxLength={8} pattern="[A-Za-z]{1,2}[0-9][A-Za-z0-9]? ?[0-9][A-Za-z]{2}" title="Enter a full UK postcode, e.g. CF10 1AA" data-testid="quote-postcode-input" />
            <FormInput label="Preferred date" type="date" min={minDate} value={formData.date1} name="date1" onChange={onFormChange} data-testid="quote-date1-input" />
            <FormInput label="Alternative date" type="date" min={minDate} value={formData.date2} name="date2" onChange={onFormChange} data-testid="quote-date2-input" />
          </div>

          <MessageField value={formData.message} onChange={onFormChange} />

          <PhotoUploader photos={photos} setPhotos={setPhotos} />

          <input type="text" name="website" value={formData.website} onChange={onFormChange} tabIndex={-1} autoComplete="off" aria-hidden="true" className="absolute -left-[9999px] h-0 w-0 opacity-0" />

          <label className="flex items-start gap-3 text-sm text-slate-600">
            <input type="checkbox" name="reviewConsent" checked={formData.reviewConsent} onChange={onFormChange} className="mt-0.5 h-5 w-5 rounded border-slate-300 text-sky-600 focus:ring-sky-600" data-testid="quote-review-consent" />
            <span>After my clean, NeatWave may email me once to ask for a review. (Optional)</span>
          </label>
          <p className="text-xs text-slate-500">We'll email you a confirmation and use your details only to handle this request. See our <a href="/privacy-policy" className="text-sky-700 underline">Privacy Policy</a>.</p>

          <button
            type="submit"
            disabled={submitting}
            data-testid="quote-submit-btn"
            className="w-full bg-sky-600 hover:bg-sky-700 text-white text-lg py-3 font-medium rounded-md shadow transition-colors disabled:opacity-60 disabled:cursor-not-allowed"
          >
            {submitting ? "Submitting..." : "Get Free Quote"}
          </button>
        </form>
      </div>
    </div>
  );
};

export default QuoteForm;
